import { Redirect, Tabs, usePathname } from 'expo-router';
import { useEffect } from 'react';
import { ActivityIndicator } from 'react-native';
import { DashboardHeader, DashboardLayout, SidebarModern } from '../../src/components/layout';
import { Box } from '../../src/components/primitives/Box';
import { useAuth } from '../../src/contexts/AuthContext';
import {
  TabsNavigationProvider,
  useTabsNavigation,
} from '../../src/contexts/TabsNavigationContext';
import { loginHrefFor } from '../../src/features/auth/returnTo';
import { useNotificationStream } from '../../src/features/notifications/hooks/useNotificationStream';
import { hasAppliedOnboarding } from '../../src/features/onboarding/appliedOrgs';
import { useOnboardingState } from '../../src/features/onboarding/hooks';
import { canReadOnboardingState } from '../../src/features/onboarding/roles';
import { useCurrentOrgRole } from '../../src/hooks/useAuthOrgRole';
import { CheckoutIntentContinuation } from '../../src/lib/billing/CheckoutIntentContinuation';
import { colors } from '../../src/theme/colors';

const ONBOARDING_ROUTE = '/onboarding';
// The gate keys off `status` ONLY, never `dismissedAt` (design.md "skip vs
// dismiss"): dismiss is a "finish later" signal, so a dismissed-but-incomplete
// owner/admin must still be re-nudged on next login. Only these two statuses
// are terminal — anything else (not_started, in_progress, or a still-loading
// undefined status) requires the gate.
const ONBOARDING_TERMINAL_STATUSES = new Set(['completed', 'skipped']);

// Each tab is its own navigator (a leaf screen or a nested Stack group), and
// the Tabs navigator keeps every tab mounted. dashboard is the default tab on
// cold open.
export const unstable_settings = {
  initialRouteName: 'dashboard',
};

// Rendered in the Tabs `tabBar` slot, which is INSIDE the navigator and so
// receives its navigation object. We capture that object (the chrome lives
// outside the navigator and can't reach it otherwise) and render no visual
// bar — navigation is driven by Sidebar + MobileTabBar.
function CaptureTabsNavigation(props: {
  readonly navigation: {
    navigate: (name: string) => void;
    dispatch: (action: { type: string; payload: object }) => void;
  };
  readonly state: { index: number; routes: ReadonlyArray<{ name: string }> };
}) {
  const tabsNav = useTabsNavigation();
  const { navigation, state } = props;
  const activeRouteName = state.routes[state.index]?.name;

  useEffect(() => {
    tabsNav?.registerTabNavigation(navigation);
  }, [tabsNav, navigation]);

  useEffect(() => {
    if (activeRouteName) tabsNav?.reportActiveTab(activeRouteName);
  }, [tabsNav, activeRouteName]);

  return null;
}

export default function AppLayout() {
  const { isAuthenticated, isLoading, currentOrganization, getAccessToken } = useAuth();
  const pathname = usePathname();
  const role = useCurrentOrgRole();
  const isGatedRole = canReadOnboardingState(role);
  const organizationId = currentOrganization?.id;
  const shouldGateOnboarding = isAuthenticated && isGatedRole && !!organizationId;

  // Always called (Rules of Hooks) — disabled via an empty id when the gate
  // doesn't apply, so the underlying query stays idle (no network call, no
  // loading flicker) for sales_rep or unauthenticated renders.
  const { data: onboardingState, isLoading: isOnboardingLoading } = useOnboardingState(
    shouldGateOnboarding ? (organizationId as string) : '',
  );

  // Mounted at the layout so an arriving notification is visible from any
  // screen. The hook existed and was called by nobody, so a WhatsApp reply
  // changed nothing anywhere unless that conversation happened to be open.
  // Muting stays where it already lives: Settings → Notifications, by category.
  useNotificationStream({
    organizationId: isAuthenticated ? organizationId : undefined,
    enabled: isAuthenticated,
    getAccessToken,
  });

  if (isLoading) {
    return (
      <DashboardLayout sidebar={<SidebarModern />} header={<DashboardHeader />}>
        <Box flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" color={colors.primary.neon} />
        </Box>
      </DashboardLayout>
    );
  }

  if (!isAuthenticated) {
    return <Redirect href={loginHrefFor(pathname)} />;
  }

  // LOADING-SAFE: never decide the redirect from an undefined/in-flight
  // onboarding-state fetch — render the loading chrome until it resolves.
  if (shouldGateOnboarding && isOnboardingLoading) {
    return (
      <DashboardLayout sidebar={<SidebarModern />} header={<DashboardHeader />}>
        <Box flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" color={colors.primary.neon} />
        </Box>
      </DashboardLayout>
    );
  }

  // Applying is transactional and the response only arrives after it commits, so
  // an apply that succeeded in this session is proof on its own. Checking it here
  // keeps the redirect off the cached state read, which is keyed by UI language,
  // restored from AsyncStorage and held fresh for minutes — any variant still
  // saying `in_progress` used to bounce a finished user straight back here.
  const needsOnboarding =
    shouldGateOnboarding &&
    !hasAppliedOnboarding(organizationId) &&
    !!onboardingState?.status &&
    !ONBOARDING_TERMINAL_STATUSES.has(onboardingState.status);

  // LOOP-SAFE: never redirect while already on /onboarding.
  if (needsOnboarding && pathname !== ONBOARDING_ROUTE) {
    return <Redirect href={ONBOARDING_ROUTE} />;
  }

  // Chrome (sidebar, header, mobile tab bar, background) is rendered ONCE here
  // and persists across navigations. The Tabs navigator swaps the focused
  // tab's content while keeping inactive tabs mounted. The provider wraps both
  // the chrome and the navigator so the captured Tabs navigation reaches the
  // chrome.
  return (
    <TabsNavigationProvider>
      <CheckoutIntentContinuation />
      <DashboardLayout sidebar={<SidebarModern />} header={<DashboardHeader />} scrollable={false}>
        <Tabs
          tabBar={(props) => <CaptureTabsNavigation {...props} />}
          screenOptions={{
            headerShown: false,
            popToTopOnBlur: true,
            // Opaque, single app-base color on every platform. Opaque is what a
            // tab navigator needs to hide the inactive (still-mounted) tabs, so
            // there's no bleed/stacking; using the same base color everywhere
            // means the scene blends with the chrome instead of reading as a
            // separate colored container.
            sceneStyle: { backgroundColor: colors.background.primary },
          }}
        />
      </DashboardLayout>
    </TabsNavigationProvider>
  );
}
